import { DatePipe, TitleCasePipe } from "@angular/common";
import { Component, computed, inject, signal, TemplateRef, viewChild } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { LocalDatePipe, RecentActivityEventDatePipe } from "@kno2/common/pipes";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { QueryOptions } from "@kno2/core/http";
import { Governance } from "@kno2/data-access/directory-api";
import { QueryHistoryService, RequestHistoryDocumentQuery } from "@kno2/data-access/query-history";
import { PaginationComponent } from "@kno2/shared/pagination";
import { TableColumn, TableComponent } from "@kno2/shared/table/table.component";
import { QueryService } from "./query.service";

@Component({
    selector: "kno2-query-history",
    imports: [TableComponent, PaginationComponent, RouterLink, LocalDatePipe, DatePipe, RecentActivityEventDatePipe],
    providers: [TitleCasePipe],
    templateUrl: "./query-history.component.html"
})
export class QueryHistoryComponent {
    private readonly router = inject(Router);
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly queryService = inject(QueryService);
    private readonly queryHistoryService = inject(QueryHistoryService);
    private readonly titleCasePipe = inject(TitleCasePipe);

    protected brandDisplayName = this.appConfig.brandDisplayName;
    protected queryDateTemplate = viewChild<TemplateRef<{ request: unknown }>>("queryDate");

    protected queryOptions = signal({
        pageSize: 10,
        pageNumber: 1
    });

    protected queryHistory = this.queryHistoryService.getQueryHistory(this.queryOptions);
    protected columns = computed(() => this.getColumns());
    protected isDeleting = false;

    private readonly governanceDisplay: Record<Governance, string> = {
        [Governance.Carequality]: "Carequality",
        [Governance.Tefca]: "TEFCA"
    };

    public resumeQuery(query: RequestHistoryDocumentQuery): void {
        this.queryService.resumeQuery(query);
        const { organizationQuery, selectedOrganizations, patientQuery, id, governance } = query || {};

        const hasOrganizationQuery = Object.values(organizationQuery).filter(Boolean).length > 1;
        const hasSelectedOrganizations = selectedOrganizations?.length;
        const hasPatientQuery = Object.values(patientQuery).filter(Boolean).length > 1;
        const isTefca = governance === Governance.Tefca;

        if (isTefca || (hasSelectedOrganizations && hasOrganizationQuery)) {
            this.router.navigate(["/records/query/new/patient"], { queryParams: { id }, state: { searchOnInit: hasPatientQuery } });
        } else if (hasOrganizationQuery) {
            this.router.navigate(["/records/query/new"], { queryParams: { id }, state: { searchOnInit: true } });
        }
    }

    public async removeHistoryEntry(id: string): Promise<void> {
        this.isDeleting = true;

        await this.queryHistoryService.removeHistoryEntry(id);

        this.isDeleting = false;
        this.queryOptionsChange({});
    }

    public queryOptionsChange(options: QueryOptions): void {
        this.queryOptions.update((currentOptions) => ({ ...currentOptions, ...options }));
    }

    private getColumns(): TableColumn<RequestHistoryDocumentQuery>[] {
        return [
            {
                field: "createDate",
                headerText: "Date",
                cellClass: "overflow-visible",
                templateRef: this.queryDateTemplate()
            },
            {
                field: "governance",
                headerText: "Governance",
                formatter: ({ governance }) => this.governanceDisplay[governance] ?? governance
            },
            {
                field: "organizationQuery",
                headerText: "Target",
                formatter: ({ organizationQuery, governance }) => (governance === Governance.Tefca ? "All QHINs" : organizationQuery?.name)
            },
            {
                field: "userName",
                headerText: "User"
            },
            {
                field: "patientQuery",
                headerText: "Patient",
                formatter: ({ patientQuery }) => `${patientQuery.firstName || ""} ${patientQuery.lastName || ""}`
            },
            {
                field: "purposeOfUseCode",
                headerText: "Purpose",
                formatter: ({ purposeOfUseCode, governance }) =>
                    governance === Governance.Carequality ? this.titleCasePipe.transform(purposeOfUseCode) : purposeOfUseCode
            }
        ];
    }
}
